import { Editor } from "grapesjs";
import { LatLngTuple, Map, PointExpression } from "leaflet";

declare global {
  interface Window {
    TalentechLeafletMap?: {
      map: Map;
      initialized: boolean;
    };
    L?: any;
  }
}

export default (editor: Editor, opts = {}) => {
  const options = {
    ...{
      blockIdMap: "leaflet-map",
      blockIdMarker: "leaflet-marker",
      category: "Leaflet",
    },
    ...opts,
  };

  editor.BlockManager.add(options.blockIdMap, {
    label: "Leaflet Map",
    category: options.category,
    media: `<i class="fa-sharp fa-light fa-map fs-2"></i>`,
    content: {
      type: "leaflet-map",
    },
  });

  editor.BlockManager.add(options.blockIdMarker, {
    label: "Leaflet Marker",
    category: options.category,
    media: `<i class="fa-sharp fa-light fa-map-pin fs-2"></i>`,
    content: {
      type: "leaflet-marker",
    },
  });

  const mapTraitsCategories = [
    {
      id: "coords",
      label: "Coordinates",
    },
    {
      id: "tiles",
      label: "Tiles",
      open: false,
    },
    {
      id: "markers",
      label: "Markers",
      open: false,
    },
  ];

  const textTraits = [
    {
      type: "text",
      name: "coords",
      label: "Coordinates",
      default: "59.9139, 10.7522",
      category: mapTraitsCategories[0],
    },
    {
      type: "number",
      name: "zoom",
      label: "Zoom",
      default: "4",
      category: mapTraitsCategories[0],
    },
    {
      type: "text",
      name: "tiles",
      label: "Tiles",
      default: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      category: mapTraitsCategories[1],
    },
    {
      type: "text",
      name: "attribution",
      label: "Attribution",
      default: "",
      category: mapTraitsCategories[1],
    },
    {
      type: "number",
      name: "maxZoom",
      label: "Max Zoom",
      default: "19",
      category: mapTraitsCategories[1],
    },
    {
      type: "number",
      name: "tileSize",
      label: "Tile Size",
      default: "512",
      category: mapTraitsCategories[1],
    },
    {
      type: "number",
      name: "zoomOffset",
      label: "Zoom Offset",
      default: "-1",
      category: mapTraitsCategories[1],
    },
    {
      type: "text",
      name: "tileId",
      label: "Tile ID",
      default: "mapbox/streets-v11",
      category: mapTraitsCategories[1],
    },
    {
      type: "text",
      name: "iconUrl",
      label: "Icon URL",
      default: "https://careersites.talentech.io/assets/images/marker.png",
      category: mapTraitsCategories[2],
    },
    {
      type: "text",
      name: "iconSize",
      label: "Icon Size",
      default: "38, 38",
      category: mapTraitsCategories[2],
    },
    {
      type: "text",
      name: "iconAnchor",
      label: "Icon Anchor",
      default: "19, 19",
      category: mapTraitsCategories[2],
    },
    {
      type: "text",
      name: "popupAnchor",
      label: "Popup Anchor",
      default: "1, -15",
      category: mapTraitsCategories[2],
    },
  ].map((trait) => ({
    ...trait,
    changeProp: true,
  }));
  const checkboxTraits = [
    {
      type: "checkbox",
      name: "dark-mode",
      label: "Dark mode",
      default: "false",
      valueTrue: "true",
      valueFalse: "false",
      category: mapTraitsCategories[1],
      changeProp: true,
    },
  ];
  const buttonTrait = {
    type: "image-selector",
    buttonText: "SELECT PIN",
    targetTrait: "iconUrl",
    category: mapTraitsCategories[2],
  };
  const traits = [...textTraits, ...checkboxTraits, buttonTrait];

  editor.DomComponents.addType("leaflet-map", {
    model: {
      defaults: {
        droppable: true,
        traits,
        script: function (opts: Record<string, string>) {
          const getCoordsFromString = (coords: string): LatLngTuple => {
            const [lat, lng] = coords.split(",").map((c) => parseFloat(c));
            return [lat, lng];
          };

          const initMap = () => {
            const mapElement = this;

            if (window.L && window.TalentechLeafletMap && window.TalentechLeafletMap.map) {
              try {
                window.TalentechLeafletMap.map.remove();
              } catch (e) {
                console.error("Error removing previous map instance from TalentechLeafletMap:", e);
              }
            }
            window.TalentechLeafletMap = undefined;

            const map = window.L.map(mapElement, {
              scrollWheelZoom: false,
              attributionControl: false,
            });

            map.on("load", function () {
              window.TalentechLeafletMap = {
                map: map,
                initialized: true,
              };
            });

            map.on('remove', function() {
              if (window.TalentechLeafletMap && window.TalentechLeafletMap.map === map) {
                window.TalentechLeafletMap = undefined;
              }
            });

            map.setView(
              getCoordsFromString(opts.coords),
              parseInt(opts.zoom)
            );
            window.L.tileLayer(opts.tiles, {
              maxZoom: parseInt(opts.maxZoom),
              id: opts.tileId,
              tileSize: parseInt(opts.tileSize),
              zoomOffset: parseInt(opts.zoomOffset),
              attribution: opts.attribution,
              referrerPolicy: "strict-origin-when-cross-origin",
            }).addTo(map);

            const tilePane = mapElement.querySelector(
              ".leaflet-tile-pane"
            ) as HTMLElement | null;
            if (tilePane) {
              tilePane.style.filter =
                opts["dark-mode"] === "true"
                  ? "invert(1) hue-rotate(180deg) grayscale(0.7)"
                  : "";
            }

            mapElement.querySelectorAll(".leaflet-marker").forEach(
              (markerEl: HTMLElement) => {
                const lat = parseFloat(
                  markerEl.getAttribute("latitude") || "0"
                );
                const lng = parseFloat(
                  markerEl.getAttribute("longitude") || "0"
                );
                const name = markerEl.getAttribute("name") || "";
                const iconUrl = markerEl.getAttribute("icon") || opts.iconUrl;
                window.L.marker([lat, lng], {
                  icon: window.L.icon({
                    iconUrl: iconUrl,
                    iconSize: getCoordsFromString(
                      opts.iconSize
                    ) as PointExpression,
                    iconAnchor: getCoordsFromString(
                      opts.iconAnchor
                    ) as PointExpression,
                    popupAnchor: getCoordsFromString(
                      opts.popupAnchor
                    ) as PointExpression,
                  }),
                })
                  .bindPopup(name)
                  .addTo(map);
              }
            );
          };

          if (typeof window.L === "undefined") {
            const leafletCss = document.createElement("link");
            leafletCss.rel = "stylesheet";
            leafletCss.href =
              "https://unpkg.com/leaflet@1.9.3/dist/leaflet.css";
            document.head.appendChild(leafletCss);

            const leafletJs = document.createElement("script");
            leafletJs.onload = () => {
              initMap();
            };
            leafletJs.src = "https://unpkg.com/leaflet@1.9.3/dist/leaflet.js";
            document.body.appendChild(leafletJs);
          } else {
            initMap();
          }
        },
        "script-props": [...textTraits, ...checkboxTraits].map(
          (trait) => trait.name
        ),
        style: {
          height: "400px",
          position: "relative",
          overflow: "hidden",
          "z-index": 0,
        },
      },
    },
  });

  editor.DomComponents.addType("leaflet-marker", {
    model: {
      defaults: {
        draggable: true,
        attributes: {
          class: "leaflet-marker",
        },
        traits: [
          {
            type: "text",
            name: "name",
            label: "Name",
          },
          {
            type: "number",
            name: "latitude",
            label: "Latitude",
            default: 51.505,
          },
          {
            type: "number",
            name: "longitude",
            label: "Longitude",
            default: -0.09,
          },
          {
            type: "text",
            name: "icon",
            label: "Icon",
          },
          {
            type: "image-selector",
            changeProp: true,
            // @ts-ignore
            buttonText: "SELECT PIN",
            targetTrait: "icon",
          },
        ],
      },
      init() {
        const name = this.getTrait("name").getValue();
        editor.Layers.setName(this, name);

        let parent = this.parent();
        while (parent && parent.get("type") !== "leaflet-map") {
          parent = parent.parent();
        }

        if (parent) {
          this.mapComponent = parent;

          if (
            window.L &&
            window.TalentechLeafletMap &&
            window.TalentechLeafletMap.map
          ) {
            try {
              window.TalentechLeafletMap.map.remove();
            } catch (e) {
              console.error("Error removing previous map instance:", e);
            }
          }
          window.TalentechLeafletMap = undefined;

          const coordsTrait = this.mapComponent.getTrait("coords");
          if (coordsTrait) {
            const currentValue = coordsTrait.getValue();
            coordsTrait.setValue(currentValue + " ");
            coordsTrait.setValue(currentValue);
          } else {
            console.warn(
              "Leaflet map 'coords' trait not found. Map might not refresh as expected."
            );
          }
        }
      },
      removed() {
        const mapComponent = this.mapComponent;

        if (mapComponent) {
          if (
            window.L &&
            window.TalentechLeafletMap &&
            window.TalentechLeafletMap.map
          ) {
            try {
              window.TalentechLeafletMap.map.remove();
            } catch (e) {
              console.error("Error removing map instance during marker removal:", e);
            }
          }
          window.TalentechLeafletMap = undefined;

          const coordsTrait = mapComponent.getTrait("coords");
          if (coordsTrait) {
            const currentValue = coordsTrait.getValue();
            coordsTrait.setValue(currentValue + " "); 
            coordsTrait.setValue(currentValue);
          } else {
            console.warn(
              "Leaflet map 'coords' trait not found on marker removal. Map might not refresh."
            );
          }
        } else {
            console.warn("Marker removed, but parent leaflet-map component not found to refresh.");
        }
      }
    },
  });
};
